import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { CalendarDays, Check, X } from "lucide-react";
import { fileHref, DetailRow } from "./common";

// Shared detail dialog for the three pending-content types (event, event
// edit, marketplace listing). Each branch builds its own layout; approve /
// reject actions reuse the review handlers from the parent and close the
// dialog on click so the admin flows back to the queue instantly.
export default function ContentDetailDialog({
  contentDetail,
  setContentDetail,
  contentDetailLoading,
  reviewEvent,
  reviewEventEdit,
  reviewListing,
}) {
  const diffRow = (label, was, now, testid) => (
    <div className="grid grid-cols-[110px_1fr_1fr] gap-2 text-xs py-1 border-b border-sage/10 last:border-b-0" data-testid={testid}>
      <span className="font-bold uppercase text-forest/50 tracking-wide">{label}</span>
      <span className="text-forest/50 line-through break-words">{was || "—"}</span>
      <span className="text-forest break-words">{now || "—"}</span>
    </div>
  );

  const renderBody = () => {
    if (!contentDetail) return null;

    if (contentDetail.type === "event") {
      const e = contentDetail.data || {};
      const photo = e.event_photo || e.image_url;
      const socialLink = (e.social_links || "").trim();
      const socialHref = socialLink && !/^https?:\/\//i.test(socialLink) ? `https://${socialLink}` : socialLink;
      const website = (e.website || "").trim();
      const websiteHref = website && !/^https?:\/\//i.test(website) ? `https://${website}` : website;
      return (
        <div className="space-y-5" data-testid="content-detail-event">
          <div className="flex flex-wrap items-start gap-4">
            {photo && <img src={fileHref(photo)} alt="Event" className="w-40 h-40 rounded-2xl object-cover border border-sage/20" data-testid="content-detail-event-photo" />}
            {e.company_logo && <img src={fileHref(e.company_logo)} alt="Logo" className="w-20 h-20 rounded-2xl object-cover border border-sage/20" data-testid="content-detail-event-logo" />}
          </div>
          <div className="bg-white/80 border border-sage/20 rounded-2xl p-4">
            <DetailRow label="Event name" value={e.event_name || e.title} testid="cd-event-name" />
            <DetailRow label="Organizer" value={e.organizer_name || e.creator_name} testid="cd-event-organizer" />
            <DetailRow label="Status" value={e.status} testid="cd-event-status" />
            <DetailRow label="Date" value={e.event_date || e.date} testid="cd-event-date" />
            <DetailRow label="Time" value={e.event_time || e.start_time} testid="cd-event-time" />
            <DetailRow label="Location" value={e.location} testid="cd-event-location" />
            <DetailRow label="Capacity" value={e.capacity} testid="cd-event-capacity" />
            <DetailRow label="Age range" value={e.age_range} testid="cd-event-age" />
            <DetailRow label="Category" value={e.category} testid="cd-event-category" />
            <DetailRow label="Contact number" value={e.contact_number} testid="cd-event-contact" />
            <DetailRow label="Email" value={e.email || e.contact_email} testid="cd-event-email" />
            {socialLink
              ? <div className="grid grid-cols-[130px_1fr] gap-3 py-1.5 border-b border-sage/10 last:border-b-0"><span className="text-xs font-bold uppercase tracking-wide text-forest/50">Social</span><a className="text-sm text-sage-dark underline break-all" href={socialHref} target="_blank" rel="noopener noreferrer">{socialLink}</a></div>
              : <DetailRow label="Social" value="" testid="cd-event-social" />}
            {website
              ? <div className="grid grid-cols-[130px_1fr] gap-3 py-1.5 border-b border-sage/10 last:border-b-0"><span className="text-xs font-bold uppercase tracking-wide text-forest/50">Website</span><a className="text-sm text-sage-dark underline break-all" href={websiteHref} target="_blank" rel="noopener noreferrer">{website}</a></div>
              : <DetailRow label="Website" value="" testid="cd-event-website" />}
            <DetailRow label="Submitted by" value={e.creator_name} testid="cd-event-submitter" />
            <DetailRow label="Created" value={e.created_at ? new Date(e.created_at).toLocaleString() : ""} testid="cd-event-created" />
          </div>
          {(e.about_event || e.description) && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">About this event</h4>
              <p className="text-sm text-forest leading-relaxed whitespace-pre-wrap bg-white/80 border border-sage/20 rounded-2xl p-4" data-testid="content-detail-event-about">{e.about_event || e.description}</p>
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full bg-sage hover:bg-sage-dark text-white" onClick={() => { reviewEvent(e.event_id, "approve"); setContentDetail(null); }} data-testid="cd-event-approve"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
            <Button size="sm" variant="outline" className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light" onClick={() => { reviewEvent(e.event_id, "reject"); setContentDetail(null); }} data-testid="cd-event-reject"><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
          </div>
        </div>
      );
    }

    if (contentDetail.type === "event_edit") {
      const ed = contentDetail.data || {};
      const p = ed.proposed || {};
      const cur = ed.current || {};
      return (
        <div className="space-y-5" data-testid="content-detail-event-edit">
          <div className="bg-white/80 border border-sage/20 rounded-2xl p-4">
            <DetailRow label="Edit for" value={ed.event_title_current} testid="cd-edit-event" />
            <DetailRow label="Submitted by" value={ed.submitted_name} testid="cd-edit-submitter" />
            <DetailRow label="Submitted" value={ed.created_at ? new Date(ed.created_at).toLocaleString() : ""} testid="cd-edit-created" />
          </div>
          <div className="rounded-xl bg-softstone/40 p-3">
            <div className="grid grid-cols-[110px_1fr_1fr] gap-2 text-[10px] font-bold uppercase tracking-wide text-forest/40 pb-1 border-b border-sage/15">
              <span>Field</span><span>Current</span><span>Proposed</span>
            </div>
            {diffRow("Organizer", cur.organizer_name, p.organizer_name, "cd-edit-row-organizer")}
            {diffRow("Event", cur.event_name || cur.title, p.event_name, "cd-edit-row-name")}
            {diffRow("About", cur.about_event || cur.description, p.about_event, "cd-edit-row-about")}
            {diffRow("Date", cur.event_date || cur.date, p.event_date, "cd-edit-row-date")}
            {diffRow("Time", cur.event_time || cur.start_time, p.event_time, "cd-edit-row-time")}
            {diffRow("Location", cur.location, p.location, "cd-edit-row-location")}
            {diffRow("Contact", cur.contact_number, p.contact_number, "cd-edit-row-contact")}
            {diffRow("Email", cur.email || cur.contact_email, p.email, "cd-edit-row-email")}
            {diffRow("Social", cur.social_links, p.social_links, "cd-edit-row-social")}
            {diffRow("Website", cur.website, p.website, "cd-edit-row-website")}
          </div>
          {(p.company_logo || p.event_photo || cur.company_logo || cur.event_photo || cur.image_url) && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">Images</h4>
              <div className="grid grid-cols-2 gap-4 text-xs">
                <div className="space-y-2">
                  <p className="text-forest/50 uppercase text-[10px]">Current</p>
                  {cur.company_logo && <img src={fileHref(cur.company_logo)} alt="Current logo" className="w-20 h-20 rounded-xl object-cover border border-sage/20" data-testid="cd-edit-cur-logo" />}
                  {(cur.event_photo || cur.image_url) && <img src={fileHref(cur.event_photo || cur.image_url)} alt="Current event photo" className="w-full max-w-xs rounded-xl object-cover border border-sage/20" data-testid="cd-edit-cur-photo" />}
                </div>
                <div className="space-y-2">
                  <p className="text-forest/50 uppercase text-[10px]">Proposed</p>
                  {p.company_logo && <img src={fileHref(p.company_logo)} alt="Proposed logo" className="w-20 h-20 rounded-xl object-cover border border-sage/20" data-testid="cd-edit-prop-logo" />}
                  {p.event_photo && <img src={fileHref(p.event_photo)} alt="Proposed event photo" className="w-full max-w-xs rounded-xl object-cover border border-sage/20" data-testid="cd-edit-prop-photo" />}
                </div>
              </div>
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full bg-sage hover:bg-sage-dark text-white" onClick={() => { reviewEventEdit(ed.edit_id, "approve"); setContentDetail(null); }} data-testid="cd-edit-approve"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
            <Button size="sm" variant="outline" className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light" onClick={() => { reviewEventEdit(ed.edit_id, "reject"); setContentDetail(null); }} data-testid="cd-edit-reject"><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
          </div>
        </div>
      );
    }

    if (contentDetail.type === "listing") {
      const l = contentDetail.data || {};
      const photos = l.photos || [];
      return (
        <div className="space-y-5" data-testid="content-detail-listing">
          {photos.length > 0 && (
            <div className="flex flex-wrap gap-3" data-testid="cd-listing-gallery">
              {photos.map((ph, i) => (
                <a key={ph} href={fileHref(ph)} target="_blank" rel="noopener noreferrer">
                  <img src={fileHref(ph)} alt={`${l.product_name} ${i + 1}`} className="w-28 h-28 rounded-xl object-cover border border-sage/20" data-testid={`cd-listing-photo-${i}`} />
                </a>
              ))}
            </div>
          )}
          <div className="bg-white/80 border border-sage/20 rounded-2xl p-4">
            <DetailRow label="Product" value={l.product_name} testid="cd-listing-name" />
            <DetailRow label="Seller" value={l.seller_name} testid="cd-listing-seller" />
            <DetailRow label="Seller role" value={l.seller_role} testid="cd-listing-seller-role" />
            <DetailRow label="Category" value={l.category} testid="cd-listing-category" />
            <DetailRow label="Price" value={l.price} testid="cd-listing-price" />
            <DetailRow label="Contact" value={l.contact} testid="cd-listing-contact" />
            <DetailRow label="Order details" value={l.order_details} testid="cd-listing-order-details" />
            <DetailRow label="Status" value={l.status} testid="cd-listing-status" />
            <DetailRow label="Seller location" value={l.seller_location} testid="cd-listing-location" />
            <DetailRow label="Created" value={l.created_at ? new Date(l.created_at).toLocaleString() : ""} testid="cd-listing-created" />
          </div>
          {l.description && (
            <div>
              <h4 className="text-xs font-bold uppercase tracking-wide text-forest/50 mb-2">Description</h4>
              <p className="text-sm text-forest leading-relaxed whitespace-pre-wrap bg-white/80 border border-sage/20 rounded-2xl p-4" data-testid="cd-listing-description">{l.description}</p>
            </div>
          )}
          <div className="flex gap-2">
            <Button size="sm" className="rounded-full bg-sage hover:bg-sage-dark text-white" onClick={() => { reviewListing(l.listing_id, "approve"); setContentDetail(null); }} data-testid="cd-listing-approve"><Check className="w-3.5 h-3.5 mr-1" /> Approve</Button>
            <Button size="sm" variant="outline" className="rounded-full border-terracotta/40 text-terracotta-dark hover:bg-terracotta-light" onClick={() => { reviewListing(l.listing_id, "reject"); setContentDetail(null); }} data-testid="cd-listing-reject"><X className="w-3.5 h-3.5 mr-1" /> Reject</Button>
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <Dialog open={!!contentDetail} onOpenChange={(o) => { if (!o) setContentDetail(null); }}>
      <DialogContent className="rounded-3xl bg-cream max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="content-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-forest flex items-center gap-2">
            <CalendarDays className="w-5 h-5 text-sage-dark" />
            <span data-testid="content-detail-title">
              {contentDetail?.type === "event" && "Pending event"}
              {contentDetail?.type === "event_edit" && "Pending event edit"}
              {contentDetail?.type === "listing" && "Pending marketplace listing"}
            </span>
          </DialogTitle>
          <DialogDescription className="text-forest/60" data-testid="content-detail-subtitle">
            {contentDetail?.type === "event" && (contentDetail?.data?.event_name || contentDetail?.data?.title)}
            {contentDetail?.type === "event_edit" && `Edit for ${contentDetail?.data?.event_title_current || ""}`}
            {contentDetail?.type === "listing" && contentDetail?.data?.product_name}
          </DialogDescription>
        </DialogHeader>
        {contentDetailLoading && (
          <div className="py-6 text-center text-sm text-forest/50" data-testid="content-detail-loading">Loading full details…</div>
        )}
        {!contentDetailLoading && renderBody()}
      </DialogContent>
    </Dialog>
  );
}
